import { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { walletSelector } from "../../redux/slices/walletSlice";
import { storeSelector } from "../../redux/slices/storeSlice";
import { authSelector } from "../../redux/slices/authSlice";
import useSpendRate from "../../hooks/useSpendRate";
import usendLogo from "../../assets/img/usendlogo.png";
import NewStoreConnectWithStoreListModal from "../../portals/customer/pages/dashboard/components/NewStoreConnectWithStoreListModal";
import { ADMIN_NAV_WIDTH, NavItem, Svg, icons } from "./AdminNavigation";
import { fetchTrackingPage } from "../../api/tracking-page";
import styles from "./adminNavigation.module.css";
import own from "./customerNavigation.module.css";

const bell = (
  <Svg>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
  </Svg>
);
const gear = (
  <Svg>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </Svg>
);
const plus = (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const mainItems = [
  { section: "dashboard", text: "Dashboard", link: "/customer/dashboard", icon: icons.dashboard },
  {
    section: "products",
    text: "Products",
    link: "/customer/products",
    icon: icons.products,
    children: [
      { route: undefined, text: "Sourcing requests", link: "/customer/products" },
      { route: "live-products", text: "Live products", link: "/customer/products/live-products" },
      { route: "quality-check", text: "Quality check", link: "/customer/products/quality-check" },
    ],
  },
  {
    section: "orders",
    text: "Orders",
    link: "/customer/orders",
    icon: icons.orders,
    children: [
      { route: undefined, text: "All orders", link: "/customer/orders" },
      { route: "order-tracking", text: "Order tracking", link: "/customer/orders/order-tracking" },
      { route: "dispute-list", text: "Disputes", link: "/customer/orders/dispute-list" },
      { route: "returned-to-overseas-warehouse", text: "Returned orders", link: "/customer/orders/returned-to-overseas-warehouse" },
    ],
  },
  { section: "payments", text: "Payments", link: "/customer/payments", icon: icons.payments },
  { section: "notifications", text: "Notifications", link: "/customer/notifications", icon: bell },
];

const parcel = (
  <Svg>
    <path d="M21 8 12 3 3 8v8l9 5 9-5z" />
    <path d="m3 8 9 5 9-5M12 13v8" />
  </Svg>
);

const trackingPageItem = { section: "tracking-page", text: "Tracking page", link: "/customer/tracking-page", icon: parcel };
const settingsItem = { section: "setting", text: "Settings", link: "/customer/setting", icon: gear };

// Beta: the backend only answers for stores that have the tracking page
// The sidebar remounts on every page: remember the answer per store, so the
// item is there straight away and "Settings" no longer moves down a row.
const trackingPageKnown = new Map();
const cacheKey = (storeId) => `usend:tracking-page:${storeId || ""}`;

function useHasTrackingPage(storeId) {
  const known = () => {
    if (trackingPageKnown.has(storeId)) return trackingPageKnown.get(storeId);
    try {
      return sessionStorage.getItem(cacheKey(storeId)) === "1";
    } catch {
      return false;
    }
  };
  const [has, setHas] = useState(known);
  useEffect(() => {
    let active = true;
    const remember = (value) => {
      trackingPageKnown.set(storeId, value);
      try {
        sessionStorage.setItem(cacheKey(storeId), value ? "1" : "0");
      } catch {
        // private mode: the in-memory answer is enough
      }
      if (active) setHas(value);
    };
    fetchTrackingPage()
      .then(() => remember(true))
      .catch(() => remember(false));
    return () => {
      active = false;
    };
  }, [storeId]);
  return has;
}

// How long the balance lasts at the recent daily spend; null when there is no spend.
function walletRunway(balance, avgDailyCost) {
  if (!avgDailyCost || avgDailyCost <= 0) return null;
  const days = balance / avgDailyCost;
  if (days < 1) return { low: true, text: "Less than a day of orders left" };
  if (days >= 30) return { low: false, text: "Covers 30+ days of orders" };
  const rounded = Math.floor(days);
  return { low: days < 3, text: `Covers about ${rounded} ${rounded === 1 ? "day" : "days"} of orders` };
}

const euro = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Customer sidebar in the admin look: store switcher, navigation, wallet.
export default function CustomerNavigation({ expanded, onToggle }) {
  const [, , section, subRoute] = useLocation().pathname.split("/");
  const { wallet } = useSelector(walletSelector);
  const { shopifyAppStore, shopifyAppUserDetails } = useSelector(storeSelector);
  const { loggedUser } = useSelector(authSelector);
  const balance = Number(wallet?.available_balance) || 0;
  const spendRate = useSpendRate(loggedUser?.active_store);
  const runway = walletRunway(balance, spendRate?.avgDailyCost);
  const noCount = () => 0;
  const storeItems = useHasTrackingPage(loggedUser?.active_store) ? [trackingPageItem, settingsItem] : [settingsItem];

  const renderItem = (item) => (
    <NavItem key={item.link} item={item} expanded={expanded} section={section} subRoute={subRoute} count={noCount} />
  );

  return (
    <aside
      aria-label="Navigation"
      className={`${styles.sidebar} ${expanded ? "" : styles.collapsed}`}
      style={{ width: expanded ? ADMIN_NAV_WIDTH.expanded : ADMIN_NAV_WIDTH.collapsed }}
    >
      <div className={styles.header}>
        {expanded ? (
          <>
            <img src={usendLogo} alt="" className={styles.logo} />
            <span className={styles.brand}>U-Send</span>
            <button type="button" className={styles.iconButton} onClick={onToggle} aria-label="Collapse sidebar">
              {icons.collapse}
            </button>
          </>
        ) : (
          <Tooltip title="Expand sidebar" placement="right" arrow>
            <button type="button" className={styles.logoButton} onClick={onToggle} aria-label="Expand sidebar">
              <img src={usendLogo} alt="" className={styles.logo} />
            </button>
          </Tooltip>
        )}
      </div>

      {expanded && (
        <div className={own.store}>
          <NewStoreConnectWithStoreListModal
            fromSidebar
            dark
            sidebarExpand
            storeOwner={shopifyAppStore?.shop_details || shopifyAppUserDetails?.shop_details}
          />
        </div>
      )}

      <nav className={styles.nav}>
        <ul className={styles.list}>{mainItems.map(renderItem)}</ul>
        {expanded ? <div className={styles.section}>Store</div> : <div className={styles.divider} />}
        <ul className={styles.list}>{storeItems.map(renderItem)}</ul>
      </nav>

      {expanded ? (
        <div className={own.wallet} aria-label="Wallet">
          <small>Wallet balance</small>
          <strong>€ {euro.format(balance)}</strong>
          {runway && <span className={runway.low ? own.runwayLow : own.runway}>{runway.text}</span>}
          <Link to="/customer/payments" className={own.topUp}>
            {plus}
            Top up
          </Link>
        </div>
      ) : (
        <div className={own.miniWallet}>
          <Tooltip title={`Wallet € ${euro.format(balance)}`} placement="right" arrow>
            <Link to="/customer/payments" className={styles.logoButton} aria-label="Wallet and top up">
              {icons.payments}
            </Link>
          </Tooltip>
        </div>
      )}
    </aside>
  );
}
